/**
 * Power-ups + comeback economy (#232, v1) — pure contract + math.
 *
 * Lives in `shared` so the engine, the simulation harness, and the client all use
 * one definition. v1 ships two cards — **Double Up** (pre-buzz; a correct answer
 * pays 2×, a wrong answer costs the normal 1×) and **Sneak Peek** (pre-buzz; a
 * private hint) — plus a deficit-weighted **comeback** acquisition roll. The race
 * cards (Brain Freeze / Snake Bite) and the new-sub-state cards (Do-Over / Tag-Team)
 * are deferred to v2 (they need the buzz-arbitration rewrite / new sub-states — see
 * docs/roadmap/232.md §10/§11). No `any`; everything here is deterministic + pure.
 */

import { normalizeAnswer } from './answer-match';

/**
 * Power-up cards. v1 shipped Double Up + Sneak Peek; v2 (#232) adds six more —
 * three race cards riding the buzz-arbitration rewrite (Steal, Brain Freeze, Snake
 * Bite), two buzz-independent payoffs (Insurance, Wager Boost), and a post-buzz
 * lifeline (Tag-Team). Extend {@link POWERUP_KINDS} and {@link POWERUP_SPECS} in
 * lockstep — the compiler pins them together.
 */
export type PowerUpKind =
  | 'DOUBLE_UP'
  | 'SNEAK_PEEK'
  | 'STEAL'
  | 'BRAIN_FREEZE'
  | 'SNAKE_BITE'
  | 'INSURANCE'
  | 'WAGER_BOOST'
  | 'TAG_TEAM';

/** Runtime list — the completeness anchor for the union (mirrors COMMAND_TYPES). */
export const POWERUP_KINDS = [
  'DOUBLE_UP',
  'SNEAK_PEEK',
  'STEAL',
  'BRAIN_FREEZE',
  'SNAKE_BITE',
  'INSURANCE',
  'WAGER_BOOST',
  'TAG_TEAM',
] as const satisfies readonly PowerUpKind[];

/** A player's hand: how many of each card they hold. Absent kind = zero. */
export type PowerUpTokens = Partial<Record<PowerUpKind, number>>;

/** How a token entered a hand (for the grant event + juice). */
export type PowerUpSource = 'starting' | 'comeback' | 'earned';

/**
 * When in a clue's lifecycle a card may be played (its "timing class"). The
 * server enforces the window; the client greys the card outside it.
 *
 *  - `PRE_BUZZ`      — armed before the buzzer opens (Double Up, Sneak Peek,
 *                      Brain Freeze, Snake Bite, Insurance).
 *  - `REOPENED_BUZZ` — only on a buzzer that reopened after a wrong answer (Steal).
 *  - `POST_BUZZ`     — after you win the buzz, while answering (Tag-Team).
 *  - `WAGER_STEP`    — at the Daily Double / Final wager step (Wager Boost).
 */
export type PowerUpTiming = 'PRE_BUZZ' | 'REOPENED_BUZZ' | 'POST_BUZZ' | 'WAGER_STEP';

/**
 * Static, per-kind legality — the single source of truth the engine, sim, and UI
 * all consult so a card's rules never drift across layers.
 *
 *  - `raceCard`   — decided by the buzzer race; auto-disabled on Daily Doubles.
 *  - `finalLegal` — may be played during Final Jeopardy (the buzz-independent ones).
 *  - `targeted`   — requires a target seat (Snake Bite).
 *  - `stealth`    — no play/target notice at open (Snake Bite): the room isn't told.
 *  - `minPlayers` — not dealt / not playable below this seat count (Tag-Team: 3).
 */
export interface PowerUpSpec {
  kind: PowerUpKind;
  timing: PowerUpTiming;
  raceCard: boolean;
  finalLegal: boolean;
  targeted: boolean;
  stealth: boolean;
  minPlayers?: number;
}

/** Per-kind legality table (see {@link PowerUpSpec}). Exhaustive over {@link PowerUpKind}. */
export const POWERUP_SPECS: Record<PowerUpKind, PowerUpSpec> = {
  DOUBLE_UP: {
    kind: 'DOUBLE_UP',
    timing: 'PRE_BUZZ',
    raceCard: false,
    finalLegal: false,
    targeted: false,
    stealth: false,
  },
  SNEAK_PEEK: {
    kind: 'SNEAK_PEEK',
    timing: 'PRE_BUZZ',
    raceCard: false,
    finalLegal: false,
    targeted: false,
    stealth: false,
  },
  STEAL: {
    kind: 'STEAL',
    timing: 'REOPENED_BUZZ',
    raceCard: true,
    finalLegal: false,
    targeted: false,
    stealth: false,
  },
  BRAIN_FREEZE: {
    kind: 'BRAIN_FREEZE',
    timing: 'PRE_BUZZ',
    raceCard: true,
    finalLegal: false,
    targeted: false,
    stealth: false,
  },
  SNAKE_BITE: {
    kind: 'SNAKE_BITE',
    timing: 'PRE_BUZZ',
    raceCard: true,
    finalLegal: false,
    targeted: true,
    stealth: true,
  },
  INSURANCE: {
    kind: 'INSURANCE',
    timing: 'PRE_BUZZ',
    raceCard: false,
    finalLegal: true,
    targeted: false,
    stealth: false,
  },
  WAGER_BOOST: {
    kind: 'WAGER_BOOST',
    timing: 'WAGER_STEP',
    raceCard: false,
    finalLegal: true,
    targeted: false,
    stealth: false,
  },
  TAG_TEAM: {
    kind: 'TAG_TEAM',
    timing: 'POST_BUZZ',
    raceCard: false,
    finalLegal: false,
    targeted: true,
    stealth: false,
    minPlayers: 3,
  },
};

/** Convenience: the spec for a kind (never undefined — the record is exhaustive). */
export function powerUpSpec(kind: PowerUpKind): PowerUpSpec {
  return POWERUP_SPECS[kind];
}

/**
 * Deficit-weighted comeback acquisition. On each correct answer the engine rolls
 * {@link dropChance}; on success the answerer draws a random deck card (respecting
 * caps). `p0` keeps the leader drawing occasionally (so a card never *diagnoses*
 * losing); `alpha`/`pMax` scale help with how far behind you are; `k` sets how much
 * of the remaining board counts as "catchable".
 */
export interface ComebackConfig {
  enabled: boolean;
  p0: number;
  alpha: number;
  pMax: number;
  k: number;
  /** Skip the roll on a Daily-Double correct answer (DD is already a swing). */
  suppressOnDailyDouble: boolean;
  /**
   * Also grant a deficit-weighted card to trailing players at the START of each board
   * round (#232, T3 iteration). The on-correct roll alone concentrates cards on the
   * skilled player (they win most buzzes → answer most → roll most); a round-start
   * grant is independent of winning the buzz, so the trailing kid — who rarely answers
   * at all — can actually receive help. The leader (gap 0) still draws only at `p0`.
   */
  roundStartGrant: boolean;
}

/**
 * Deterministic auto-award economy (#232 v2, F7). On top of the probabilistic
 * comeback roll, grant a FIXED number of comeback cards to trailing players (never
 * the leader): `atStart` at game start and `throughout` spread across board-round
 * starts. Deterministic (persisted seed + counter) and recovery-safe — a persisted
 * `autoAwardedThroughout` count means recovery never re-grants. `atStart: 0,
 * throughout: 0` disables it (the v1 behavior).
 */
export interface AutoAwardConfig {
  enabled: boolean;
  /** Cards handed to trailing players at game start (most-behind first). */
  atStart: number;
  /** Cards handed to trailing players spread across the game (round starts). */
  throughout: number;
}

/** Per-game power-up configuration (resolved at creation; part of the snapshot). */
export interface PowerUpConfig {
  enabled: boolean;
  /** Cards that may be drawn / played. */
  deck: PowerUpKind[];
  /**
   * Cards every player starts with (a fixed set), used ONLY when
   * {@link startingCardCount} is absent. When a count is set, each player instead gets a
   * deterministic per-player RANDOM draw of that many distinct kinds and this list is
   * ignored (kept as the fallback for Classic-style fixed decks + older configs).
   */
  startingHand: PowerUpKind[];
  /**
   * #232 (item 6): when set, deal each player a RANDOM draw of this many DISTINCT deck
   * kinds at seat time (deterministic per seat — see {@link drawStartingHand} — so hands
   * vary but replay identically and recovery never re-deals). Overrides
   * {@link startingHand}. Absent ⇒ fall back to the fixed `startingHand` list. Clamped to
   * the deck size (you can't draw more distinct kinds than exist).
   */
  startingCardCount?: number;
  /** Max total tokens a player may hold. */
  deckCap: number;
  /** Max of any single kind a player may hold. */
  perKindCap: number;
  comeback: ComebackConfig;
  /** Brain Freeze / Snake Bite buzz delay applied to affected seats (ms). */
  freezeMs: number;
  /** Tag-Team answer-window extension — deliberately larger than a normal lifeline (ms). */
  tagTeamMs: number;
  /**
   * Tag-Team helper bonus — the share of a clue's value the tagged teammate banks when
   * the caster answers CORRECTLY (upside only; a wrong answer pays the teammate nothing).
   * The caster still keeps the full clue value. `0.5` = the teammate earns +50% of the
   * clue value. This is what gives the teammate a reason to help. `0` disables the bonus.
   */
  tagTeamBonusPct: number;
  /**
   * Wager Boost die faces → wager-ceiling boost as a fraction of the current max.
   * A roll picks a face uniformly; the new ceiling is `floor(max · (1 + face))`.
   * Length is the number of die faces (default six).
   */
  wagerBoostFaces: number[];
  /** Deterministic auto-award economy (F7). */
  autoAward: AutoAwardConfig;
}

/**
 * Game-level power-up state, part of the persisted {@link GameState} (#232). `seed`
 * is derived from the game id at creation; `rollCounter` advances on every comeback
 * roll so the sequence is deterministic and recovery never re-rolls (the outcome is
 * captured by the persisted counter + the granted tokens in each hand).
 */
export interface PowerUpGameState {
  config: PowerUpConfig;
  seed: number;
  rollCounter: number;
  /**
   * F7 auto-award bookkeeping (recovery-safe). `atStartDone` flips once the
   * game-start grants are dispensed; `throughoutDone` counts the spread grants
   * already handed out, so a round-start pass never re-grants and recovery restores
   * both from the snapshot rather than re-running the economy.
   */
  autoAward?: { atStartDone: boolean; throughoutDone: number };
}

/** Default die faces — six-sided, boost = 25%…200% of the current wager ceiling. */
export const DEFAULT_WAGER_BOOST_FACES: number[] = [0.25, 0.5, 0.75, 1.0, 1.5, 2.0];

/** Classic preset — power-ups off. The default; the base game is untouched. */
export const DEFAULT_POWERUP_CONFIG: PowerUpConfig = {
  enabled: false,
  deck: [...POWERUP_KINDS],
  startingHand: [],
  deckCap: 3,
  perKindCap: 2,
  comeback: {
    enabled: false,
    p0: 0.1,
    alpha: 0.45,
    pMax: 0.55,
    k: 0.5,
    suppressOnDailyDouble: true,
    roundStartGrant: false,
  },
  freezeMs: 750,
  tagTeamMs: 7000,
  tagTeamBonusPct: 0.5,
  wagerBoostFaces: [...DEFAULT_WAGER_BOOST_FACES],
  autoAward: { enabled: false, atStart: 0, throughout: 0 },
};

/**
 * "Game Night" preset — the full v2 deck + comeback + auto-award on. Every player starts
 * with a deterministic RANDOM draw of {@link startingCardCount} distinct cards (default 4;
 * host-configurable 0–8 in the lobby), so hands differ per player and per game; the
 * comeback roll + deterministic F7 auto-award (leader excluded) then top trailing hands
 * back up. The deck cap leaves headroom for those grants on top of a full 8-card start.
 */
export const GAME_NIGHT_POWERUP_CONFIG: PowerUpConfig = {
  enabled: true,
  deck: [...POWERUP_KINDS],
  startingHand: [],
  startingCardCount: 4,
  deckCap: 12,
  perKindCap: 2,
  comeback: {
    enabled: true,
    p0: 0.1,
    alpha: 0.45,
    pMax: 0.55,
    k: 0.5,
    suppressOnDailyDouble: true,
    roundStartGrant: true,
  },
  freezeMs: 750,
  tagTeamMs: 7000,
  tagTeamBonusPct: 0.5,
  wagerBoostFaces: [...DEFAULT_WAGER_BOOST_FACES],
  autoAward: { enabled: true, atStart: 1, throughout: 2 },
};

// ---------------------------------------------------------------------------
// Hand math (pure)
// ---------------------------------------------------------------------------

/** Total tokens held. */
export function handTotal(tokens: PowerUpTokens): number {
  return (Object.values(tokens) as number[]).reduce((sum, n) => sum + (n ?? 0), 0);
}

/** True if `kind` can be added to `tokens` without breaching either cap. */
export function canGrant(
  tokens: PowerUpTokens,
  kind: PowerUpKind,
  cfg: PowerUpConfig,
): boolean {
  if (handTotal(tokens) >= cfg.deckCap) return false;
  return (tokens[kind] ?? 0) < cfg.perKindCap;
}

/** Pure add — returns a new hand with one more `kind` (does NOT enforce caps). */
export function addToken(tokens: PowerUpTokens, kind: PowerUpKind): PowerUpTokens {
  return { ...tokens, [kind]: (tokens[kind] ?? 0) + 1 };
}

/**
 * Dedicated seed offset so per-player starting-hand draws live in their own deterministic
 * sub-stream, disjoint from the comeback/auto-award `rollCounter` stream (which stays at 0
 * until the first real roll). Keeps the two concerns independent + recovery-safe.
 */
const STARTING_HAND_SEED_OFFSET = 0x9e3779b1;

/**
 * #232 (item 6): a deterministic per-player RANDOM starting hand — `count` DISTINCT kinds
 * drawn from `deck` via a partial Fisher–Yates shuffle over a per-player sub-stream keyed by
 * `(seed, playerIndex)`. Distinct so a fresh hand never contains a duplicate; capped at the
 * deck size. Pure + reproducible: the same `(deck, count, seed, playerIndex)` always yields
 * the same hand, so recovery re-derives (or simply reuses the persisted tokens) identically
 * and it never disturbs the comeback roll stream.
 */
export function drawStartingHand(
  deck: readonly PowerUpKind[],
  count: number,
  seed: number,
  playerIndex: number,
): PowerUpKind[] {
  const pool = [...deck];
  const n = Math.max(0, Math.min(Math.floor(count), pool.length));
  const playerSeed =
    (seed + STARTING_HAND_SEED_OFFSET + Math.imul(playerIndex >>> 0, 0x85ebca77)) >>> 0;
  const kinds: PowerUpKind[] = [];
  for (let i = 0; i < n; i += 1) {
    const r = rollAt(playerSeed, i);
    const j = i + Math.min(pool.length - 1 - i, Math.floor(r * (pool.length - i)));
    const a = pool[i];
    const b = pool[j];
    if (a === undefined || b === undefined) continue;
    pool[i] = b;
    pool[j] = a;
    kinds.push(b);
  }
  return kinds;
}

/** Pure spend — returns a new hand with one fewer `kind` (clamped at 0). */
export function spendToken(tokens: PowerUpTokens, kind: PowerUpKind): PowerUpTokens {
  const next = Math.max(0, (tokens[kind] ?? 0) - 1);
  const out: PowerUpTokens = { ...tokens };
  if (next === 0) delete out[kind];
  else out[kind] = next;
  return out;
}

// ---------------------------------------------------------------------------
// Comeback math (pure)
// ---------------------------------------------------------------------------

/**
 * Normalized "how far behind" in `[0,1]`: `gap-to-leader / (k · remainingBoardValue)`,
 * clamped. The leader (gap 0) is 0; a player more than `k`× the remaining board behind
 * saturates at 1. `remainingBoardValue` is the sum of unrevealed clue face values.
 */
export function behindScore(
  score: number,
  leaderScore: number,
  remainingBoardValue: number,
  k: number,
): number {
  const gap = Math.max(0, leaderScore - score);
  const denom = Math.max(1, k * remainingBoardValue);
  return Math.min(1, gap / denom);
}

/** P(draw a card) on a correct answer: `clamp(p0 + alpha·b, 0, pMax)`. */
export function dropChance(
  cfg: ComebackConfig,
  score: number,
  leaderScore: number,
  remainingBoardValue: number,
): number {
  if (!cfg.enabled) return 0;
  const b = behindScore(score, leaderScore, remainingBoardValue, cfg.k);
  return Math.max(0, Math.min(cfg.pMax, cfg.p0 + cfg.alpha * b));
}

/**
 * Deterministic PRNG (mulberry32). Seeded per game (see {@link seedFromGameId}) and
 * advanced by a persisted counter so a comeback roll is reproducible AND recovery
 * never re-rolls (the outcome is captured by the persisted counter + granted tokens).
 */
export function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return function next(): number {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), a | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** Stable 32-bit seed from a game id (FNV-1a) so a game's rolls are reproducible. */
export function seedFromGameId(gameId: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < gameId.length; i += 1) {
    h ^= gameId.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

/**
 * One deterministic draw at `counter`: returns a float in [0,1). The engine keeps a
 * monotonic `rollCounter`; each roll uses `(seed + counter)` so replays match.
 */
export function rollAt(seed: number, counter: number): number {
  return mulberry32((seed + counter) >>> 0)();
}

// ---------------------------------------------------------------------------
// Sneak Peek hint (pure)
// ---------------------------------------------------------------------------

/** One word of the answer as revealed by Sneak Peek: its first letter + length. */
export interface SneakPeekWord {
  first: string;
  length: number;
}

/** The private Sneak Peek hint: response prefix + first-letter/length of every word. */
export interface SneakPeekHint {
  prefix: string;
  words: SneakPeekWord[];
}

/**
 * Build the Sneak Peek hint from a clue's host-only `response`. Reuses
 * {@link normalizeAnswer} to strip the "what is"/article phrasing down to the core
 * answer, then reveals the first letter + length of each word. The displayed prefix
 * is "Who is" when the response reads as a person ("Who …"), else "What is".
 */
export function sneakPeekHint(response: string): SneakPeekHint {
  const core = normalizeAnswer(response);
  const words: SneakPeekWord[] = core.length
    ? core
        .split(' ')
        .map((w) => ({ first: (w[0] ?? '').toUpperCase(), length: w.length }))
    : [];
  const lower = response.trim().toLowerCase();
  const prefix = /^(who|whom|who's|whos)\b/.test(lower) ? 'Who is' : 'What is';
  return { prefix, words };
}

// ---------------------------------------------------------------------------
// Wager Boost die (pure)
// ---------------------------------------------------------------------------

/** Outcome of a Wager Boost roll: the 1-based die face and its WIN multiplier. */
export interface WagerBoostRoll {
  /** 1-based face shown to the player (`faceIndex + 1`). */
  face: number;
  /** Win multiplier applied to a CORRECT wager payout (e.g. `1.5` = +50%). */
  multiplier: number;
}

/**
 * Map a uniform `roll` in `[0,1)` to a die face + its win multiplier. Each face's
 * {@link PowerUpConfig.wagerBoostFaces} entry is a fractional bonus; the multiplier is
 * `1 + fraction` (so `0.5` → `1.5×`). Deterministic — the caller draws `roll` from the
 * seeded per-game stream ({@link rollAt}) so the result is reproducible and recovery
 * never re-rolls.
 */
export function wagerBoostRoll(faces: number[], roll: number): WagerBoostRoll {
  const n = faces.length;
  if (n === 0) return { face: 1, multiplier: 1 };
  const idx = Math.min(n - 1, Math.max(0, Math.floor(roll * n)));
  return { face: idx + 1, multiplier: 1 + Math.max(0, faces[idx] ?? 0) };
}

/** Apply a win multiplier to a wager payout: `round(wager · multiplier)`. */
export function boostedPayout(wager: number, multiplier: number): number {
  return Math.round(wager * Math.max(1, multiplier));
}

// ---------------------------------------------------------------------------
// Buzz arbitration (pure) — the buffer-and-compare core (#232 v2, Phase 0)
// ---------------------------------------------------------------------------

/** One buffered buzz attempt: who buzzed and when (monotonic ms). */
export interface BuzzAttempt {
  playerId: string;
  arrivalMs: number;
}

/**
 * Decide a buzz race by EFFECTIVE time = `arrivalMs + offsetMs[playerId]` (offset 0
 * when absent). The minimum effective time wins; ties break by earliest raw arrival,
 * then by buffer order (stable). Returns the winning playerId, or `undefined` for an
 * empty buffer. Pure + deterministic so the engine, sim, and tests agree — this is
 * the core Brain Freeze / Snake Bite / Steal offsets ride on.
 */
export function arbitrateBuzz(
  attempts: readonly BuzzAttempt[],
  offsetMs: Readonly<Record<string, number>>,
): string | undefined {
  let best: { playerId: string; effective: number; arrivalMs: number } | undefined;
  for (const a of attempts) {
    const effective = a.arrivalMs + (offsetMs[a.playerId] ?? 0);
    if (
      best === undefined ||
      effective < best.effective ||
      (effective === best.effective && a.arrivalMs < best.arrivalMs)
    ) {
      best = { playerId: a.playerId, effective, arrivalMs: a.arrivalMs };
    }
  }
  return best?.playerId;
}

// ---------------------------------------------------------------------------
// Auto-award selection (pure) — F7 deterministic comeback grants
// ---------------------------------------------------------------------------

/** A player's identity + score, the only inputs auto-award selection needs. */
export interface ScoreRow {
  id: string;
  score: number;
}

/**
 * Choose `count` recipients for deterministic comeback grants: trailing players only
 * (never anyone at the leader's score), most-behind first, cycling round-robin when
 * `count` exceeds the number of trailing players. Ties break by id for stability.
 * Returns an ordered list of playerIds (with repeats allowed under round-robin) — the
 * caller applies caps + picks a card kind from the seeded stream. Empty when there is
 * no trailing player (everyone tied for the lead) or `count <= 0`.
 *
 * `includeAllIfTied` handles the game-START case: at a dead-even opening there is no
 * leader to exclude, so (when set) grants spread round-robin across ALL seats instead
 * of nobody. Mid-game throughout grants leave it off — a tie means no deficit, no grant.
 */
export function selectTrailingForAward(
  players: readonly ScoreRow[],
  count: number,
  opts: { includeAllIfTied?: boolean } = {},
): string[] {
  if (count <= 0 || players.length === 0) return [];
  const leaderScore = players.reduce(
    (max, p) => Math.max(max, p.score),
    Number.NEGATIVE_INFINITY,
  );
  const byScore = (a: ScoreRow, b: ScoreRow): number =>
    a.score - b.score || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0);
  let pool = players.filter((p) => p.score < leaderScore).sort(byScore);
  if (pool.length === 0) {
    // Everyone is tied for the lead: no leader to exclude. At game start we still want
    // to seed comeback cards, so fall back to all seats; otherwise grant nobody.
    if (!opts.includeAllIfTied) return [];
    pool = [...players].sort(byScore);
  }
  const out: string[] = [];
  for (let i = 0; i < count; i += 1) {
    const row = pool[i % pool.length];
    if (row) out.push(row.id);
  }
  return out;
}
